import { useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import { useStoreContext } from './useStoreContext';
import { useEventCallback } from '../useEventCallback';


/**
 * @example
 * const StoreSetter = ({ name }) => {
    const [value, setValue] = useStore(name);
    return (
        <>
            <dt>{name}</dt>
            <dd>
                <input
                    type="text"
                    value={value}
                    onChange={e =>
                        setValue(
                            e.target.value === '' ? undefined : e.target.value
                        )
                    }
                />
            </dd>
        </>
    );
};

export const Basic = () => {
    return (
        <StoreContextProvider value={memoryStore()}>
            <h1>Setter</h1>
            <dl>
                <StoreSetter name="foo.bar" />
                <StoreSetter name="foo.baz" />
            </dl>
        </StoreContextProvider>
    );
};
 * const ChangeDensity: FC<any> = () => {
 *     const [density, setDensity] = useStore('posts.list.density', 'small');
 *
 *     const changeDensity = (): void => {
 *         setDensity(density === 'small' ? 'medium' : 'small');
 *     };
 *
 *     return (
 *         <Button onClick={changeDensity}>
 *             {`Change density (current ${density})`}
 *         </Button>
 *     );
 * };
 */
/**
 * 
 * @param key 全局上下文key，按页面级key命名，防止冲突
 * @param defaultValue 默认值
 * @returns 一个小型的状态管理，上下文数据,解决了context上下文容器某个数据变化，整个链条会render，只有引用的组件状态变化，才会带动关联的上下文一起render，
 * 减少render，提升性能，可以使用localstroage（需要注意数据大小）管理或者context注入数据
 */
export const useStore = <T = any>(
  key: string,
  defaultValue?: T
): useStoreResult<T> => {
  const { getItem, setItem, subscribe } = useStoreContext();
  const [value, setValue] = useState(() => getItem(key, defaultValue));

  // subscribe to changes on this key, and change the state when they happen
  useEffect(() => {
    const storedValue = getItem(key, defaultValue);
    if (!isEqual(value, storedValue)) {
      setValue(storedValue);
    }
    const unsubscribe = subscribe(key, newValue => {
      setValue(typeof newValue === 'undefined' ? defaultValue : newValue);
    });
    return () => unsubscribe();
  }, [key, subscribe, defaultValue, getItem, value]);

  const set = useEventCallback(
    (valueParam: T, runtimeDefaultValue: T) => {
      const newValue =
                typeof valueParam === 'function'
                  ? valueParam(value)
                  : valueParam;
      // we only set the value in the Store;
      // the value in the local state will be updated
      // by the useEffect during the next render
      setItem(
        key,
        typeof newValue === 'undefined'
          ? typeof runtimeDefaultValue === 'undefined'
            ? defaultValue
            : runtimeDefaultValue
          : newValue
      );
    },
    [key, setItem, defaultValue, value]
  );
  return [value, set];
};

export type useStoreResult<T = any> = [
  T,
  (value: T | ((value: T) => void), defaultValue?: T) => void
];
